import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/AppShell";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { useModules, useSubject } from "@/lib/query-hooks";
import { LayersIcon } from "lucide-react";

export const Route = createFileRoute("/exam/$examSlug/subject/$subjectSlug")({
  head: ({ params }) => ({
    meta: [
      {
        title: `${params.subjectSlug} · ${params.examSlug.toUpperCase()} — PN-CivilServices`,
      },
      {
        name: "description",
        content: `Modules for ${params.subjectSlug} under ${params.examSlug.toUpperCase()}: PYQs, NCERTs, standard books, study materials, and handwritten notes.`,
      },
      {
        property: "og:title",
        content: `${params.subjectSlug} · ${params.examSlug.toUpperCase()}`,
      },
      {
        property: "og:description",
        content: `Study modules for ${params.subjectSlug}.`,
      },
    ],
  }),
  component: SubjectPage,
});

const DEFAULT_MODULES: Array<{ key: string; name: string }> = [
  { key: "pyqs", name: "PYQs" },
  { key: "ncerts", name: "NCERTs" },
  { key: "standard-books", name: "Standard Books" },
  { key: "study-materials", name: "Study Materials" },
  { key: "handwritten-notes", name: "Handwritten Notes" },
];

function SubjectPage() {
  const { examSlug, subjectSlug } = Route.useParams();
  const { data: subject } = useSubject(examSlug, subjectSlug);
  const { data: modules } = useModules(subject?.id);

  const list = modules?.length
    ? modules
    : DEFAULT_MODULES.map((m) => ({ ...m, rootDirectoryId: m.key, fileCount: 0 }));

  return (
    <AppShell>
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { label: "Ledger", to: "/" },
            { label: examSlug.toUpperCase(), to: `/exam/${examSlug}` },
            { label: subject?.name ?? subjectSlug },
          ]}
        />

        <header className="mt-8">
          <div className="label-card">Subject</div>
          <h1 className="mt-1 font-display text-4xl font-semibold">
            {subject?.name ?? subjectSlug}
          </h1>
          {subject?.description ? (
            <p className="mt-2 max-w-2xl text-ink-soft">
              {subject.description}
            </p>
          ) : null}
        </header>

        <div className="mt-10 mb-3 flex items-center justify-between">
          <div className="label-card">Modules</div>
          <div className="text-xs text-ink-muted flex items-center gap-1">
            <LayersIcon className="h-3.5 w-3.5" /> Filed by category
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((m) => (
            <Link
              key={m.key}
              to="/exam/$examSlug/subject/$subjectSlug/$moduleKey/$"
              params={{
                examSlug,
                subjectSlug,
                moduleKey: m.key,
                _splat: "",
              }}
              className="card-ledger group p-5 hover:border-paprika transition"
            >
              <div className="label-card">Module</div>
              <div className="mt-1 font-display text-2xl font-semibold group-hover:text-paprika transition">
                {m.name}
              </div>
              <div className="mt-3 text-xs text-ink-muted">
                {m.fileCount ?? 0} files filed
              </div>
              <div className="mt-6 text-xs text-paprika font-semibold uppercase tracking-widest">
                Open →
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* If backend redirects to canonical case, allow it — no-op here */}
      {false && <Navigate to="/" />}
    </AppShell>
  );
}
